import Button from "@components/Button"
import FunnelHeader from "@components/quiz/FunnelHeader"
import FunnelLayout from "@components/quiz/FunnelLayout"
import StatusIcon from "@components/StatusIcon"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import * as Sentry from "@sentry/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useAsyncError, useRouteError } from "react-router-dom"

interface Props {
  error: unknown
  // NOTE: Off where the caller has reported the error already (Sentry's own
  // error boundary).
  report?: boolean
}

// Takes the error as a prop and uses no router hooks, so it also renders
// outside the router (App's error boundary), where `useRouteError` used to
// throw (Sentry PRODUCTION-S7).
export default function ErrorPage({ error, report = true }: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    // NOTE: Nothing to report without an error, and capturing `null` only
    // gives an "Error: null" event that says nothing (PRODUCTION-9).
    if (report && error != null) Sentry.captureException(error)
  }, [error, report])

  const details = getDetails(error)

  return (
    <FunnelLayout header={<FunnelHeader />}>
      <div className="flex grow animate-fade-up flex-col justify-center gap-6 pb-16">
        <StatusIcon icon={ExclamationTriangleIcon} tone="red" />

        <div className="space-y-3">
          <h1 className="vm-heading">{t("pages.error.oops")}</h1>

          <p className="vm-subheading">{t("pages.error.anErrorOccurred")}</p>
        </div>

        {details && (
          <p className="break-words rounded-vm-md bg-vm-surface/60 p-3 text-center font-mono text-xs text-vm-muted">
            {details}
          </p>
        )}

        <Button
          onClick={() => window.location.reload()}
          title={t("pages.error.tryAgain")}
        />
      </div>
    </FunnelLayout>
  )
}

/** A route's `errorElement`. */
export function RouteErrorPage() {
  return <ErrorPage error={useRouteError()} />
}

/**
 * An `<Await>`'s `errorElement`. The rejected promise's error is only
 * available from `useAsyncError`: `useRouteError` returns `null` there, which
 * is what "Error: null" on /checkout/plans was (PRODUCTION-9).
 */
export function AsyncErrorPage() {
  return <ErrorPage error={useAsyncError()} />
}

function getDetails(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return

  const { message, statusText } = error as {
    message?: unknown
    statusText?: unknown
  }

  const details = statusText ?? message

  return typeof details === "string" ? details : undefined
}
